import { Checkbox, Modal, Space, Spin, Switch, useToast } from '@bfe/moly';
import { LoadingIcon } from '@bfe/moly-icons';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

import useTgBindNotice from '@/hooks/useTgBindNotice';
import { useI18nContext } from '@/i18n/I18n';
import {
  getTelegramP2PBindStatus,
  setTelegramP2PBind,
} from '@/services/setting';
import { TG_NOTICE_SUBSCRIBE_STATUS } from '@/utils/constants/setting';

const TgBind = () => {
  const [$t] = useI18nContext();
  const { toast, EToastType } = useToast();
  const [noticeLoading, setNoticeLoading] = useState(true);
  const [visibleBindSuccessModal, setVisibleBindSuccessModal] = useState(false);
  const [visibleUnbindSuccessModal, setVisibleUnbindSuccessModal] =
    useState(false);
  const [tgNoticeList, setTgNoticeList] = useState({
    ORDER: {
      isSubscribed: false,
      key: 'p2pNoticeTrade',
      loading: false,
    },
    APPEAL: {
      isSubscribed: false,
      key: 'p2pNoticeAppeal',
      loading: false,
    },
  });

  const getP2PNoticeStatus = () => {
    setNoticeLoading(true);
    getTelegramP2PBindStatus()
      .then((res) => {
        const { notice_info = [] } = res || { tgCategoryList: [] };
        const list = { ...tgNoticeList };
        notice_info.map(({ notice_biz, notice_tg }: any) => {
          if (Object.keys(list).includes(notice_biz)) {
            list[notice_biz as keyof typeof tgNoticeList].isSubscribed =
              notice_tg === TG_NOTICE_SUBSCRIBE_STATUS.SUBSCRIBED;
          }
        });
        setTgNoticeList(list);
      })
      .finally(() => {
        setNoticeLoading(false);
      });
  };

  const { unBind, binding, isBind, botName } = useTgBindNotice({
    bindCb: ({ result }) => {
      if (result) {
        setVisibleBindSuccessModal(true);
      }
    },
    unbindCb: ({ result }) => {
      if (result) {
        getP2PNoticeStatus();
      }
    },
  });

  const handleUnbind = () => {
    setVisibleUnbindSuccessModal(false);
    unBind();
  };

  const handleP2p = ({ type, status }: { type: string; status: boolean }) => {
    setTgNoticeList((list) => {
      list[type as keyof typeof tgNoticeList].loading = true;
      return { ...list };
    });
    setTelegramP2PBind({
      notice_biz: type,
      notice_tg: status
        ? TG_NOTICE_SUBSCRIBE_STATUS.SUBSCRIBED
        : TG_NOTICE_SUBSCRIBE_STATUS.UNSUBSCRIBE,
    })
      .then(() => {
        setTgNoticeList((list) => {
          list[type as keyof typeof tgNoticeList].loading = false;
          list[type as keyof typeof tgNoticeList].isSubscribed = status;
          return { ...list };
        });
      })
      .catch((error) => {
        const msg = error?.ret_msg;
        const code = error?.ret_code || '';
        const reason = `${code}: ${
          $t(`${code}`, { ns: 'inverse-error-code' }) !== `${code}`
            ? $t(`${code}`, { ns: 'inverse-error-code' })
            : msg
        }`;
        toast({
          iconType: EToastType.FAIL,
          title: reason,
        });
        setTgNoticeList((list) => {
          list[type as keyof typeof tgNoticeList].loading = false;
          return { ...list };
        });
      });
  };

  useEffect(() => {
    getP2PNoticeStatus();
  }, []);

  if (!botName) {
    return null;
  }

  return (
    <>
      <div className="notice-setup-form-item-switch-container tg-bind-container">
        <div className="notice-setup-form-item-item">
          <div className="switch-label">
            <div className="notice-name">{$t('telegramNoticeSwitch')}</div>
          </div>
          <div className={classNames('widget-container', { bound: isBind })}>
            <Switch
              loading={binding}
              checked={isBind}
              onCheckedChange={
                (checked: boolean) => {
                  if (!checked) {
                    setVisibleUnbindSuccessModal(true);
                  }
                }
                // handleSwitchNotify(checked, item.id)
              }
            />
            <div id="widget-container"></div>
          </div>
        </div>
        <div className="tg-notice-list">
          <Space direction="vertical" size={16}>
            {Object.keys(tgNoticeList).map((type) => {
              const { key, isSubscribed, loading } =
                tgNoticeList[type as keyof typeof tgNoticeList];
              return (
                <Space key={type} size={4}>
                  <Checkbox
                    checked={isSubscribed}
                    className="transaction-setup-form-item-checkbox"
                    onChange={() => {
                      handleP2p({ type, status: !isSubscribed });
                    }}
                    disabled={!isBind || loading || noticeLoading}
                  >
                    <span className="text-[14px]">{$t(key)}</span>
                  </Checkbox>
                  <Spin
                    wrapperClassName="transaction-setup-form-item-checkbox-loading"
                    indicator={
                      <LoadingIcon className="transaction-setup-form-item-checkbox-loading-icon animate-spin" />
                    }
                    spinning={loading || noticeLoading}
                  >
                    <div className="transaction-setup-form-item-checkbox-loading-container" />
                  </Spin>
                </Space>
              );
            })}
          </Space>
        </div>
      </div>
      {/* 绑定成功弹窗 */}
      <Modal
        type="successful"
        titleCenter
        size="small"
        title={
          <h1 style={{ paddingLeft: '32px', paddingRight: '32px' }}>
            {$t('telegramBindSuccessTitle')}
          </h1>
        }
        subTitle={
          <div style={{ paddingLeft: '32px', paddingRight: '32px' }}>
            {$t('telegramBindSuccessDesc')}
          </div>
        }
        onOk={() => setVisibleBindSuccessModal(false)}
        onCancel={() => setVisibleBindSuccessModal(false)}
        open={visibleBindSuccessModal}
        cancelText={false}
        closable={false}
        okText={$t('Confirm')}
      />
      {/* 解绑确认弹窗 */}
      <Modal
        type="alert"
        closable={false}
        titleCenter={true}
        title={
          <h1 style={{ paddingLeft: '32px', paddingRight: '32px' }}>
            {$t('telegramUnbindTitle')}
          </h1>
        }
        subTitle={
          <div style={{ paddingLeft: '32px', paddingRight: '32px' }}>
            {$t('telegramUnbindDesc')}
          </div>
        }
        onOk={() => {
          handleUnbind();
        }}
        onCancel={() => setVisibleUnbindSuccessModal(false)}
        open={visibleUnbindSuccessModal}
        cancelText={$t('Cancel')}
        okText={$t('Confirm')}
      />
    </>
  );
};

export default TgBind;
